import { EUserNotificationType, TSharePointMedia, TUserNotification } from '../ducks/api-ducks/user-notifications-duck/types-user-notifications';

export const ALLOWED_TEAM_NAMES = ['IncentiveCompensationGuide'] as const;

/**
 * Parses a SharePoint embed code (iframe HTML) and extracts the TeamName and UniqueId.
 * Only accepts URLs from allowed SharePoint teams.
 * Returns undefined if parsing fails or the team name is not allowed.
 */
export const parseSharePointEmbedCode = (embedCode: string): TSharePointMedia | undefined => {
  if (!embedCode || typeof embedCode !== 'string') {
    return undefined;
  }

  // Extract src attribute from iframe
  const srcMatch = embedCode.match(/src=["']([^"']+)["']/i);
  if (!srcMatch?.[1]) {
    return undefined;
  }

  const srcUrl = srcMatch[1];

  // Extract team name from URL pattern: /teams/{TeamName}/_layouts/
  const teamNameMatch = srcUrl.match(/\/teams\/([^/]+)\/_layouts\//i);
  if (!teamNameMatch?.[1]) {
    return undefined;
  }

  const teamName = teamNameMatch[1];

  // Validate team name is in allowed list
  if (!ALLOWED_TEAM_NAMES.map((name) => name.toLowerCase()).includes(teamName.toLowerCase())) {
    return undefined;
  }

  // Extract UniqueId query parameter
  try {
    const url = new URL(srcUrl);
    const uniqueId = url.searchParams.get('UniqueId');
    if (!uniqueId) {
      return undefined;
    }

    return { teamName, uniqueId };
  } catch {
    return undefined;
  }
};

/**
 * Builds a SharePoint embed URL from a TSharePointMedia object.
 */
export const buildSharePointEmbedUrl = (media: TSharePointMedia): string =>
  `https://microsoft.sharepoint.com/teams/${media.teamName}/_layouts/15/embed.aspx?UniqueId=${media.uniqueId}`;

export type TSplashScreenRouting = { phase: 'splash' } | { phase: 'banner'; notification: TUserNotification };

/**
 * Determines whether a SplashScreen notification should display as a splash screen or transition to a banner.
 * If transitioning to banner and the notification has SharePoint media, appends a media link via Quill Delta operations.
 * @param now - Current date, injected for testability
 */
export const resolveSplashScreenPhase = (notification: TUserNotification, mediaLinkText: string, now: Date = new Date()): TSplashScreenRouting => {
  const splashEnd = notification.splashEndDate ? new Date(notification.splashEndDate) : null;
  const endDate = notification.notificationEndDate ? new Date(notification.notificationEndDate) : null;

  if (splashEnd && now > splashEnd && endDate && now <= endDate) {
    const bannerNotification: TUserNotification = { ...notification, notificationType: EUserNotificationType.Banner };
    bannerNotification.richMessage = getRichMessageForNotification(bannerNotification, mediaLinkText);

    return { phase: 'banner', notification: bannerNotification };
  }

  return { phase: 'splash' };
};

export const getRichMessageForNotification = (notification: TUserNotification, mediaLinkText: string): string | undefined => {
  if (!notification.sharePointMedia?.teamName || !notification.sharePointMedia?.uniqueId) {
    return notification.richMessage;
  }
  try {
    const mediaUrl = buildSharePointEmbedUrl(notification.sharePointMedia);
    const delta: { ops: any[] } = JSON.parse(notification.richMessage || '{"ops":[]}');
    delta.ops.push({ insert: ' ' }, { insert: mediaLinkText, attributes: { link: mediaUrl } }, { insert: '\n' });
    return JSON.stringify(delta);
  } catch {
    // If we fail to parse/append link just return the original richMessage.
    return notification.richMessage;
  }
};
